/* ============================================================================
   Furniva 360 spin viewer and AR hand-off.

   The spin is frame-swapping, not a video: pointer drag, touch swipe, a range
   input and arrow keys all move the same frame index, so the control is usable
   with a mouse, a finger or a keyboard alone.

   AR uses Shopify's own <model-viewer>; this file only reveals the stage and
   calls the platform's activateAR(). No third-party AR library is loaded.
   ============================================================================ */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ AR -- */
  var arButton = document.querySelector('[data-fv-ar]');
  var arStage = document.querySelector('[data-fv-ar-stage]');

  // The <model-viewer> element is defined by Shopify's viewer library, which
  // nothing else on this page loads. Load it on first use (version from the
  // fallback build from the tag's data-shopify-feature), then reveal the model.
  function loadViewer(viewer) {
    return new Promise(function (resolve) {
      if (window.customElements && window.customElements.get('model-viewer')) return resolve();
      var version = (viewer && viewer.getAttribute('data-shopify-feature')) || '1.12';
      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        // Same feature Horizon's gallery loads; it brings <model-viewer> with it.
        window.Shopify.loadFeatures([{ name: 'model-viewer-ui', version: '1.0', onLoad: function () { resolve(); } }]);
      } else {
        // Shopify's loader missing: the documented CDN build, same as it serves.
        var s = document.createElement('script');
        s.type = 'module';
        s.src = 'https://cdn.shopify.com/shopifycloud/model-viewer/v' + version + '/model-viewer.js';
        s.onload = resolve; s.onerror = resolve;
        document.head.appendChild(s);
      }
      // Never leave the button doing nothing.
      setTimeout(resolve, 6000);
    });
  }

  if (arButton && arStage) {
    var viewer = arStage.querySelector('model-viewer');
    var lastFocus = null;

    function close() {
      arStage.hidden = true;
      arStage.classList.remove('is-open');
      document.documentElement.classList.remove('fv-ar3d-open');
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    function open() {
      lastFocus = document.activeElement;
      arStage.hidden = false;
      document.documentElement.classList.add('fv-ar3d-open');
      window.requestAnimationFrame(function () { arStage.classList.add('is-open'); });
      var closeBtn = arStage.querySelector('.fv-ar3d__close');
      if (closeBtn) closeBtn.focus();
      arButton.setAttribute('aria-busy', 'true');
      loadViewer(viewer).then(function () {
        arButton.removeAttribute('aria-busy');
        if (!viewer || !window.customElements) return;
        window.customElements.whenDefined('model-viewer').then(function () {
          // Phones with AR open the room view straight away; closing it
          // leaves the 3D window, which is also the view everywhere else.
          if (viewer.canActivateAR && typeof viewer.activateAR === 'function') {
            try { viewer.activateAR(); } catch (e) { /* stay in 3D */ }
          }
        });
      });
    }

    arButton.addEventListener('click', open);
    Array.prototype.forEach.call(arStage.querySelectorAll('[data-fv-ar-close]'), function (el) {
      el.addEventListener('click', close);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !arStage.hidden) close();
    });
    // Warm the library when the button is about to be used.
    ['pointerenter', 'touchstart', 'focus'].forEach(function (ev) {
      arButton.addEventListener(ev, function () { loadViewer(viewer); }, { once: true, passive: true });
    });
  }

  /* ---------------------------------------------------------------- spin -- */
  var stage = document.querySelector('[data-fv-spin-stage]');
  if (!stage) return;

  var frames = Array.prototype.slice.call(stage.querySelectorAll('.fv-spin__frame'));
  var range = document.querySelector('[data-fv-spin-range]');
  if (frames.length < 2) return;

  var index = 0;

  function show(next) {
    var count = frames.length;
    index = ((next % count) + count) % count;
    frames.forEach(function (frame, i) {
      frame.classList.toggle('is-on', i === index);
    });
    if (range && Number(range.value) !== index) range.value = index;
  }

  /* ---- drag / swipe ---- */
  var dragging = false;
  var startX = 0;
  var startIndex = 0;

  function begin(x) {
    dragging = true;
    startX = x;
    startIndex = index;
    stage.classList.add('is-dragging');
  }

  function move(x) {
    if (!dragging) return;
    var width = stage.getBoundingClientRect().width || 1;
    // A full drag across the stage turns the piece exactly once.
    var delta = (x - startX) / width;
    show(startIndex + Math.round(delta * frames.length));
  }

  function end() {
    dragging = false;
    stage.classList.remove('is-dragging');
  }

  stage.addEventListener('pointerdown', function (event) {
    if (event.button !== 0 && event.pointerType === 'mouse') return;
    begin(event.clientX);
    if (stage.setPointerCapture) {
      try { stage.setPointerCapture(event.pointerId); } catch (e) { /* not capturable */ }
    }
  });
  stage.addEventListener('pointermove', function (event) { move(event.clientX); });
  ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (evt) {
    stage.addEventListener(evt, end);
  });

  // Touch, for browsers without pointer events.
  stage.addEventListener('touchstart', function (event) {
    if (event.touches.length !== 1) return;
    begin(event.touches[0].clientX);
  }, { passive: true });
  stage.addEventListener('touchmove', function (event) {
    if (!dragging || event.touches.length !== 1) return;
    move(event.touches[0].clientX);
    // Only once a drag is genuinely underway, so vertical scrolling still works.
    if (event.cancelable) event.preventDefault();
  }, { passive: false });
  stage.addEventListener('touchend', end);

  /* ---- keyboard / slider ---- */
  if (range) {
    range.addEventListener('input', function () { show(Number(range.value)); });
    range.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') { event.preventDefault(); show(index - 1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); show(index + 1); }
    });
  }

  show(0);
})();
